// src/store/features/analyticsKbArticlePerformance.js

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import dayjs from 'dayjs';
import { ROOT_URL } from '../../api/ApiConfig'; // adjust path to your config
import {
  applyAnalyticsPendingLoading,
  markAnalyticsFetchKey,
} from '../../utils/analyticsQuery';

// ───────────────────────────────────────────────
// Helper: detect preset range type (same logic as your tag stats)
const detectRangeType = (startDate, endDate) => {
  const start = dayjs(startDate);
  const end = dayjs(endDate);
  const now = dayjs();

  if (!start.isValid() || !end.isValid()) return 'custom';
  const diffDays = end.diff(start, 'day');

  if (diffDays === 0) {
    if (end.isSame(now, 'day')) return 'today';
    if (end.isSame(now.subtract(1, 'day'), 'day')) return 'yesterday';
    return 'custom';
  }

  if (!end.isSame(now, 'day')) return 'custom';

  if (diffDays === 6 && start.isSame(now.subtract(6, 'day'), 'day')) return '7d';
  if (diffDays === 29 && start.isSame(now.subtract(29, 'day'), 'day')) return '30d';
  if (diffDays === 89 && start.isSame(now.subtract(89, 'day'), 'day')) return '90d';
  if (diffDays === 364 && start.isSame(now.subtract(364, 'day'), 'day')) return '1y';

  return 'custom';
};

// ───────────────────────────────────────────────
// Thunk: Fetch article performance
// ───────────────────────────────────────────────
export const getKbArticlePerformance = createAsyncThunk(
  'kbArticlePerformance/getKbArticlePerformance',
  async (params = {}, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const token = state.loginToken?.response?.token;
      const businessId = state.loginToken?.response?.user?.business_id;

      if (!businessId) {
        throw new Error('Authentication or business ID missing');
      }

      const timezone = params.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Karachi';

      let range = params.range;
      let startDate = params.startDate;
      let endDate = params.endDate;
      let page = params.page ?? 1;
      let limit = params.limit ?? 5;
      const group_id = params.group_id;

      // Default to last 7 days if nothing provided
      if (!startDate || !endDate) {
        endDate = dayjs().format('YYYY-MM-DD');
        startDate = dayjs().subtract(6, 'day').format('YYYY-MM-DD');
        range = '7d';
      }

      if (!range) {
        range = detectRangeType(startDate, endDate);
      }

      // Build query string
      let query = `range=${range}&page=${page}&timezone=${encodeURIComponent(timezone)}&limit=${limit}`;

      if (range === 'custom' && startDate && endDate) {
        query += `&from=${startDate}&to=${endDate}`;
      }
      if (group_id) {
        query += `&group_id=${encodeURIComponent(group_id)}`;
      }

      const url = `${ROOT_URL}/analytics/kb/article-performance/${businessId}?${query}`;

      console.log('[API] Fetching article performance →', url);

      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      return response.data;
    } catch (err) {
      console.error('Article performance fetch failed:', err);
      return rejectWithValue(
        err.response?.data?.message || 'Failed to load article performance data'
      );
    }
  }
);

// ───────────────────────────────────────────────
// Thunk: Initialize with default range (usually 7d or 30d)
// ───────────────────────────────────────────────
export const initKbArticlePerformance = createAsyncThunk(
  'kbArticlePerformance/initKbArticlePerformance',
  async ({ group_id } = {}, { dispatch }) => {
    // You can change default range here (today / 7d / 30d ...)
    const end = dayjs().endOf('day');
    const start = dayjs().subtract(29, 'day').startOf('day'); // example: 30 days

    await dispatch(
      getKbArticlePerformance({
        startDate: start.format('YYYY-MM-DD'),
        endDate: end.format('YYYY-MM-DD'),
        range: '7d',
        page: 1,
        limit: 5,
        group_id,
      })
    );
  }
);

// ───────────────────────────────────────────────
// Slice
// ───────────────────────────────────────────────
const initialState = {
  loading: false,
  error: null,
  data: null,
  lastFetchKey: null,
  articles: [],
  pagination: {
    page: 1,
    limit: 5,
    totalArticles: null, // null until the endpoint reports a total
  },
  range: '7d',
  dateRange: {
    startDate: null,
    endDate: null,
  },
};

const kbArticlePerformanceSlice = createSlice({
  name: 'kbArticlePerformance',
  initialState,
  reducers: {
    resetArticlePerformance: () => initialState,

    setArticlePerformancePage: (state, action) => {
      state.pagination.page = action.payload;
    },

    setArticlePerformanceDateRange: (state, action) => {
      const { startDate, endDate } = action.payload;
      state.dateRange = { startDate, endDate };
      state.range = detectRangeType(startDate, endDate);
    },

    clearArticlePerformanceError: (state) => {
      state.error = null;
    },
  },

  extraReducers: (builder) => {
    builder
      // ── getKbArticlePerformance ───────────────────────────────
      .addCase(getKbArticlePerformance.pending, (state, action) => {
        applyAnalyticsPendingLoading(state, action.meta?.arg);
        state.error = null;
      })
      .addCase(getKbArticlePerformance.fulfilled, (state, action) => {
        state.loading = false;
        markAnalyticsFetchKey(state, action.meta?.arg);
        const payload = action.payload;

        if (payload?.data) {
          state.data = payload.data;
          state.articles = payload.data.articles || [];
          state.range = payload.data.range || state.range;

          state.dateRange = {
            startDate: payload.data.from ? dayjs(payload.data.from).format('YYYY-MM-DD') : null,
            endDate: payload.data.to ? dayjs(payload.data.to).format('YYYY-MM-DD') : null,
          };

          // `null` means the endpoint did not report a total, which is not the
          // same as reporting zero; the footer needs to tell those apart.
          const reportedTotal =
            payload.data.total ??
            payload.data.totalArticles ??
            payload.data.totalCount ??
            null;

          state.pagination = {
            page: payload.data.page || 1,
            limit: payload.data.limit || 5,
            totalArticles:
              reportedTotal != null && Number.isFinite(Number(reportedTotal))
                ? Number(reportedTotal)
                : null,
          };
        }
      })
      .addCase(getKbArticlePerformance.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.articles = [];
      })

      // ── init ───────────────────────────────────────────────────
      .addCase(initKbArticlePerformance.pending, (state, action) => {
        applyAnalyticsPendingLoading(state, {
          range: '7d',
          group_id: action.meta?.arg?.group_id,
          page: 1,
          limit: 5,
        });
      })
      .addCase(initKbArticlePerformance.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(initKbArticlePerformance.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const {
  resetArticlePerformance,
  setArticlePerformancePage,
  setArticlePerformanceDateRange,
  clearArticlePerformanceError,
} = kbArticlePerformanceSlice.actions;

export default kbArticlePerformanceSlice.reducer;